import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    enquiryType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, enquiryType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          enquiry_type: formData.enquiryType || null,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send enquiry');
      }

      const data = await response.json();
      
      toast.success('Enquiry Sent!', {
        description: data.message || "We'll be in touch soon.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        enquiryType: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send enquiry', {
        description: 'Please try again or contact us directly at sales@advancedadas.com.au',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Contact Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Get in Touch
            </h2>
            <p className="text-slate-400 text-base sm:text-lg mb-10 leading-relaxed">
              Ready to book a calibration or have questions about our services? 
              Contact us directly or fill out the enquiry form.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <a 
                href="tel:0402454588" 
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Phone className="w-5 h-5 text-blue-400 group-hover:text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Phone</p>
                  <p className="text-white font-semibold">0402 454 588</p>
                </div>
              </a>

              <a 
                href="mailto:sales@advancedadas.com.au" 
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                  <Mail className="w-5 h-5 text-blue-400 group-hover:text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <p className="text-white font-semibold">sales@advancedadas.com.au</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Service Area</p>
                  <p className="text-white font-semibold">South Australia</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Business Hours</p>
                  <p className="text-white font-semibold">Mon - Fri: 8am - 5pm</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Enquiry Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form 
              onSubmit={handleSubmit} 
              className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 md:p-8"
              data-testid="contact-form"
            >
              <h3 className="font-heading text-xl font-bold text-white mb-6">
                Enquiry Form
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="name" className="text-slate-300 mb-2 block">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your name"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-300 mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0400 000 000"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-slate-300 mb-2 block">
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>

              <div className="mb-4">
                <Label htmlFor="enquiryType" className="text-slate-300 mb-2 block">
                  Enquiry Type
                </Label>
                <Select onValueChange={handleSelectChange} value={formData.enquiryType}>
                  <SelectTrigger 
                    className="bg-slate-900/50 border-slate-600 text-white focus:ring-blue-500"
                    data-testid="contact-select-type"
                  >
                    <SelectValue placeholder="Select enquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calibration">Request Calibration</SelectItem>
                    <SelectItem value="trade">Trade Enquiry</SelectItem>
                    <SelectItem value="quote">Quote Request</SelectItem>
                    <SelectItem value="general">General Enquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="message" className="text-slate-300 mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="Tell us about your calibration requirements..."
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-blue-500/20 transition-all"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  'Preparing...'
                ) : (
                  <>
                    Send Enquiry
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
